import { useQuery } from '@tanstack/react-query';
import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { AutostartSetting } from '@/features/autostart/autostart';
import { NotificationSettingsSection } from '@/features/notifications/notification-settings';
import { usePlatform } from '@/lib/platform/context';
import type { HotkeyInfo } from '@/lib/platform/types';
import { hyprlandSnippet } from './compositor-snippet';
import { HOTKEYS_KEY, HotkeyBindings } from './hotkey-bindings';

type CopyState = 'idle' | 'copied' | 'failed';

function CompositorSnippet({ info }: { info: HotkeyInfo }) {
  const { t } = useTranslation('settings');
  const [copy, setCopy] = useState<CopyState>('idle');
  const snippet = hyprlandSnippet(info.bindings);

  async function onCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopy('copied');
    } catch {
      setCopy('failed');
    }
  }

  return (
    <div className="space-y-3">
      <pre className="overflow-x-auto rounded-md border bg-muted p-4 text-sm">
        <code>{snippet}</code>
      </pre>
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={() => void onCopy()}>
          {copy === 'copied' ? (
            <Check aria-hidden="true" className="h-4 w-4" />
          ) : (
            <Copy aria-hidden="true" className="h-4 w-4" />
          )}
          {t('hotkeys.wayland.copy')}
        </Button>
        <output aria-live="polite" className="text-sm text-muted-foreground">
          {copy === 'copied' && t('hotkeys.wayland.copied')}
          {copy === 'failed' && t('hotkeys.wayland.copyFailed')}
        </output>
      </div>
    </div>
  );
}

/**
 * PRD VS-050 settings: which hotkeys are live, conflicts reported, and — on
 * Wayland, where the app cannot grab keys — the compositor config to copy
 * instead of an editable field.
 */
function HotkeySection() {
  const { t } = useTranslation('settings');
  const platform = usePlatform();
  const hotkeys = platform.hotkeys;
  const query = useQuery({
    queryKey: HOTKEYS_KEY,
    queryFn: () => (hotkeys ? hotkeys() : Promise.resolve(null)),
    enabled: hotkeys !== undefined,
  });

  if (!hotkeys) return <p className="text-muted-foreground">{t('hotkeys.webOnly')}</p>;
  if (query.isPending) {
    return (
      <output aria-live="polite" className="flex items-center gap-2 text-muted-foreground">
        <Spinner />
        {t('hotkeys.loading')}
      </output>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Alert variant="destructive" role="alert">
        {t('hotkeys.error')}
      </Alert>
    );
  }

  const info = query.data;
  if (info.capabilities.requiresExternalTrigger) {
    return (
      <div className="space-y-3">
        <h3 className="font-medium">{t('hotkeys.wayland.title')}</h3>
        <p className="text-muted-foreground">{t('hotkeys.wayland.description')}</p>
        <CompositorSnippet info={info} />
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <HotkeyBindings info={info} setHotkeys={platform.setHotkeys} />
      {info.session === 'x11' && (
        <details className="space-y-3">
          <summary className="cursor-pointer text-sm">{t('hotkeys.wayland.x11OptIn')}</summary>
          <CompositorSnippet info={info} />
        </details>
      )}
    </div>
  );
}

export function SettingsPage() {
  const { t } = useTranslation('settings');
  const { autostart } = usePlatform();
  return (
    <div className="max-w-3xl space-y-8">
      <h1 className="text-2xl font-semibold">{t('title')}</h1>
      <section aria-labelledby="settings-hotkeys" className="space-y-3">
        <div>
          <h2 id="settings-hotkeys" className="text-lg font-semibold">
            {t('hotkeys.title')}
          </h2>
          <p className="text-sm text-muted-foreground">{t('hotkeys.description')}</p>
        </div>
        <HotkeySection />
      </section>
      <NotificationSettingsSection />
      {autostart && (
        <section aria-labelledby="settings-autostart" className="space-y-3">
          <h2 id="settings-autostart" className="text-lg font-semibold">
            {t('autostart.title')}
          </h2>
          <AutostartSetting />
        </section>
      )}
    </div>
  );
}
